import { useState } from 'react';

import { bankAccountsApi, outletsApi, paymentsApi, profileApi } from '@pukka/api';
import { InformationBox, PageContainer, Shimmer, Text } from '@pukka/ui-components';
import { DefaultActionBar, GopayActivationForm } from '@pukka/ui-composite';
import { useFlagWithFallback } from '@pukka/unleash';
import { useQuery } from '@tanstack/react-query';

import gopayLogo from './assets/gopay-logo.svg';
import banner from './assets/gopay-qris-banner.svg';
import { EwalletInformationBox } from './EwalletInformationBox';
import { EwalletStatusButtons } from './EwalletStatusButtons';
import { EwalletStatusModal } from './EwalletStatusModal';
import { EwalletStatusSummary } from './EwalletStatusSummary';
import { QRISGoPayIntroText } from './QRISGoPayIntroText';
import { combineOutletNameAndStatus, getInactiveOutlets, getStatusCount } from './utils';

function QRISByGoPayPage() {
  const [showDetail, setShowDetail] = useState(false);
  const [openActivationForm, setOpenActivationForm] = useState(false);

  const { data: profileData } = profileApi.useProfile();

  const { data: eWalletsData, isLoading: isEWalletsLoading } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => paymentsApi.getAllEWalletPaymentMethods(),
  });

  const { data: outletsData, isLoading: isOutletsLoading } = useQuery({
    queryKey: ['outlet-list'],
    queryFn: () => outletsApi.getOutletList(),
  });

  const { data: bankListData } = useQuery({
    queryKey: ['bank-list'],
    queryFn: () => bankAccountsApi.getBankList(),
  });

  const isLoading = isEWalletsLoading || isOutletsLoading;

  const gopayData = eWalletsData?.find((ewallet) => ewallet.code === 'gopay');
  const outletStatuses = gopayData?.outlet_statuses;
  const statusCount = getStatusCount(outletsData, outletStatuses);
  const isTrial = profileData?.business?.is_trial;
  const isAllInactive = !outletStatuses || statusCount.inactive === outletsData?.length;
  const statusDetailData = combineOutletNameAndStatus(outletsData, outletStatuses);
  const inactiveOutlets = getInactiveOutlets(outletsData, outletStatuses);

  const [enableGopayRegistration] = useFlagWithFallback('backoffice-enable-gopay-registration');

  const handleClickSeeDetail = () => {
    setShowDetail(true);
  };

  const handleClickRegister = () => {
    if (showDetail) {
      setShowDetail(false);
    }

    setOpenActivationForm(true);
  };

  return (
    <PageContainer className="max-w-[100vw]">
      <DefaultActionBar pageTitle="QRIS" />
      <div className="lg:mb-8">
        <img src={banner} alt="Terima pembayaran apa aja lebih mudah pakai QRIS" className="w-full rounded-xl" />
      </div>
      <div className="flex flex-col lg:flex-row lg:gap-5 ">
        <QRISGoPayIntroText />

        <div className="flex h-fit flex-1 flex-col gap-[10px] rounded-xl bg-neutral-50 p-6">
          <Text variant="title-1" weight="medium">
            QRIS Registration
          </Text>
          <div>
            <Text variant="body-2" className="text-neutral-600">
              Accept cashless transactions from various digital payment providers with just one QRIS code.
              <br />
              -----------
            </Text>
            <Text variant="body-1" className="text-neutral-600" as="i">
              Terima transaksi non-tunai dari berbagai layanan pembayaran digital dengan satu kode QRIS saja.
            </Text>
          </div>
          <div className="flex items-center gap-4 rounded-xl bg-white px-4 py-2">
            <img src={gopayLogo} alt="" height={56} width={56} />
            <div>
              <Text variant="body-2">MDR: 0.7%</Text>
              <Text className="text-neutral-600" variant="caption-2">
                Settlement cycle: 1 business day
              </Text>
            </div>
          </div>

          {isLoading && <Shimmer size="xxl" className="h-[72px] w-full" />}

          {!isLoading && !isAllInactive && <EwalletStatusSummary statusCount={statusCount} />}

          <EwalletInformationBox isLoading={isLoading} isTrial={isTrial} />

          <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
            {isLoading ? (
              <Shimmer size="xxl" className="w-full" />
            ) : (
              <EwalletStatusButtons
                isAllInactive={isAllInactive}
                isTrial={profileData?.business?.is_trial}
                hasInactive={statusCount.inactive > 0}
                disableRegistration={!enableGopayRegistration}
                onClickSeeDetail={handleClickSeeDetail}
                onClickRegister={handleClickRegister}
              />
            )}
          </div>
          {!isLoading && !enableGopayRegistration && (
            <InformationBox variant="danger" icon="maintenance">
              This feature is currently under maintenance. Please check back again once maintenance is complete. We appreciate your
              patience!
            </InformationBox>
          )}
        </div>
      </div>

      {statusDetailData && (
        <EwalletStatusModal
          open={showDetail}
          data={statusDetailData}
          disableRegistration={!enableGopayRegistration}
          onToggleModal={setShowDetail}
          onClickRegister={handleClickRegister}
        />
      )}

      {profileData && inactiveOutlets && bankListData && (
        <GopayActivationForm
          open={openActivationForm}
          onOpenChange={setOpenActivationForm}
          profileData={profileData}
          outlets={inactiveOutlets}
          bankList={bankListData}
        />
      )}
    </PageContainer>
  );
}

export { QRISByGoPayPage };
